/* Cosmoody theme script: projected skies, buy boxes, cart drawer, small UI bits. */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const C = window.Cosmoody || {};
  const routes = C.routes || { cartAdd: '/cart/add', cart: '/cart', cartChange: '/cart/change' };
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const TAU = Math.PI * 2;
  const MARK = '<svg viewBox="0 0 64 64" aria-hidden="true"><path d="M53.06 38.35A22 22 0 1 1 31.02 10.03A18 18 0 1 0 53.06 38.35Z"/><path d="M50 7Q51.2 14.8 59 16Q51.2 17.2 50 25Q48.8 17.2 41 16Q48.8 14.8 50 7Z"/></svg>';
  const money = cents => {
    try { return new Intl.NumberFormat(C.locale || 'en', { style: 'currency', currency: C.currency || 'USD' }).format(cents / 100); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  };
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

  /* ---------- projected sky renderer ---------- */
  function mulberry32(a) { return () => { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
  function hashStr(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
  function gauss(R) { let u = 0, v = 0; while (!u) u = R(); while (!v) v = R(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(TAU * v); }

  class Sky {
    constructor(c) {
      this.c = c; this.ctx = c.getContext('2d');
      this.kind = c.dataset.kind || 'moon';
      this.bg = c.dataset.bg || '#14122B';
      this.full = c.dataset.full === '1';
      this.scale = parseFloat(c.dataset.scale || '0.94');
      this.cxf = parseFloat(c.dataset.cx || '0.5');
      this.cyf = parseFloat(c.dataset.cy || '0.5');
      this.seed = hashStr(c.id || 'sky');
      this.off = document.createElement('canvas');
      this.o = this.off.getContext('2d');
      this.visible = false; this.ready = false;
      if ('ResizeObserver' in window) new ResizeObserver(() => this.resize()).observe(c);
      else this.resize();
    }
    setKind(k) { this.kind = k; this.build(); this.draw(performance.now()); }
    resize() {
      const r = this.c.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;
      const d = Math.min(window.devicePixelRatio || 1, 1.75);
      this.w = Math.round(r.width * d); this.h = Math.round(r.height * d);
      this.c.width = this.w; this.c.height = this.h;
      this.D = this.full ? Math.hypot(this.w, this.h) * 1.05 : Math.min(this.w, this.h) * this.scale;
      this.S = Math.max(64, Math.min(Math.round(this.D), 1000));
      this.off.width = this.off.height = this.S;
      this.build(); this.draw(performance.now());
    }
    build() {
      if (!this.S) return;
      const S = this.S, u = S / 600, c = S / 2, R0 = S * .27;
      const R = mulberry32(this.seed ^ hashStr(this.kind));
      const dens = { moon: .45, earth: .55, nebula: 1.1, milkyway: .7, saturn: .7, aurora: .9, galaxy: .8, stars: 2.4, universe: 1.2 }[this.kind] || .7;
      const n = Math.round(300 * dens * (S / 600) ** 2);
      this.stars = Array.from({ length: n }, () => ({ x: R() * S, y: R() * S, r: (0.35 + R() ** 4 * 1.6) * u, a: .25 + R() * .75, p: R() * TAU, s: .4 + R() * 1.3 }));
      this.bright = Array.from({ length: this.kind === 'stars' ? 16 : 5 }, () => ({ x: c + (R() - .5) * S * .8, y: c + (R() - .5) * S * .8, r: (1.4 + R() * 1.2) * u }));
      if (this.kind === 'moon') {
        this.maria = Array.from({ length: 8 }, () => ({ x: c + (R() - .55) * R0 * 1.1, y: c + (R() - .6) * R0 * 1.1, rx: R0 * (.14 + R() * .22), ry: R0 * (.1 + R() * .18), rot: R() * 3 }));
        this.craters = Array.from({ length: 34 }, () => { const a = R() * TAU, d = Math.sqrt(R()) * R0 * .92; return { x: c + Math.cos(a) * d, y: c + Math.sin(a) * d, r: R0 * (.018 + R() ** 2 * .09) }; });
      }
      if (this.kind === 'earth') {
        this.land = [];
        for (let k = 0; k < 5; k++) {
          const a = R() * TAU, d = Math.sqrt(R()) * R0 * .75, cx = c + Math.cos(a) * d, cy = c + Math.sin(a) * d;
          for (let j = 0; j < 10; j++) this.land.push({ x: cx + gauss(R) * R0 * .13, y: cy + gauss(R) * R0 * .13, r: R0 * (.05 + R() * .11), desert: R() < .25 });
        }
        this.clouds = Array.from({ length: 16 }, () => ({ x: c + (R() - .5) * R0 * 1.8, y: c + (R() - .5) * R0 * 1.8, rx: R0 * (.12 + R() * .25), ry: R0 * (.03 + R() * .05), rot: (R() - .5) * .6 }));
      }
      if (this.kind === 'nebula') {
        const cols = ['255,111,94', '155,123,255', '47,191,165', '255,178,150', '120,96,230'];
        this.gas = Array.from({ length: 11 }, (_, i) => ({ x: c + gauss(R) * S * .13, y: c + gauss(R) * S * .13, r: S * (.12 + R() * .2), col: cols[i % cols.length], a: .16 + R() * .22, f: R() * TAU }));
        this.dust = Array.from({ length: 4 }, () => ({ x: c + gauss(R) * S * .12, y: c + gauss(R) * S * .12, r: S * (.06 + R() * .08) }));
      }
      if (this.kind === 'milkyway') {
        const m = Math.round(1500 * (S / 600) ** 2 * .6);
        this.band = Array.from({ length: m }, () => ({ x: (R() - .5) * S * 1.2, y: gauss(R) * S * .07, r: (.3 + R() ** 3 * .9) * u, a: .25 + R() * .6 }));
      }
      if (this.kind === 'galaxy') {
        const m = Math.round(2600 * (S / 600) ** 2 * .5);
        this.spiral = Array.from({ length: m }, () => {
          const arm = R() < .5 ? 0 : Math.PI, k = R() ** .65, rad = k * S * .42;
          const ang = arm + k * 5.2 + gauss(R) * (.18 + .25 * (1 - k));
          const warm = 1 - k;
          return { x: Math.cos(ang) * rad, y: Math.sin(ang) * rad, r: (.35 + R() ** 3 * 1.1) * u, a: .3 + R() * .6,
            col: warm > .6 ? '255,226,190' : (R() < .5 ? '170,190,255' : '205,170,255') };
        });
      }
      if (this.kind === 'universe') {
        const cols = ['155,123,255', '255,111,94', '47,191,165'];
        this.gas = Array.from({ length: 4 }, (_, i) => ({ x: c + gauss(R) * S * .16, y: c + gauss(R) * S * .16, r: S * (.18 + R() * .16), col: cols[i % 3], a: .09 + R() * .08, f: R() * TAU }));
        this.galaxies = Array.from({ length: 7 }, () => ({ x: c + (R() - .5) * S * .72, y: c + (R() - .5) * S * .72, rx: S * (.025 + R() * .05), tilt: R() * Math.PI, flat: .25 + R() * .5, warm: R() < .5 }));
      }
      if (this.kind === 'aurora') {
        this.curtains = [{ ph: R() * 6, col: '94,227,200', a: .2 }, { ph: R() * 6, col: '47,191,165', a: .16 }, { ph: R() * 6, col: '160,245,210', a: .1 }];
      }
      this.ready = true;
    }
    drawStars(o, T) {
      for (const s of this.stars) {
        const tw = reduced ? 1 : .72 + .28 * Math.sin(T * .0011 * s.s + s.p);
        o.fillStyle = 'rgba(255,248,236,' + (s.a * tw).toFixed(3) + ')';
        o.beginPath(); o.arc(s.x, s.y, s.r, 0, TAU); o.fill();
      }
    }
    spikes(o) {
      const u = this.S / 600;
      o.strokeStyle = 'rgba(255,246,236,.55)'; o.lineWidth = .8 * u;
      for (const b of this.bright) {
        o.fillStyle = 'rgba(255,250,244,.95)'; o.beginPath(); o.arc(b.x, b.y, b.r, 0, TAU); o.fill();
        o.beginPath(); o.moveTo(b.x - b.r * 6, b.y); o.lineTo(b.x + b.r * 6, b.y); o.moveTo(b.x, b.y - b.r * 6); o.lineTo(b.x, b.y + b.r * 6); o.stroke();
      }
    }
    draw(t) {
      if (!this.ready || !this.w) return;
      const { ctx, o, S } = this, c = S / 2, T = reduced ? 0 : t;
      o.globalCompositeOperation = 'source-over';
      o.fillStyle = '#0d0b24'; o.fillRect(0, 0, S, S);
      this.drawStars(o, T);
      (this['k_' + this.kind] || this.k_moon).call(this, o, T, c, S / 600);
      o.globalCompositeOperation = 'destination-in';
      const g = o.createRadialGradient(c, c, c * (this.full ? .5 : .74), c, c, c);
      g.addColorStop(0, 'rgba(0,0,0,1)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      o.fillStyle = g; o.fillRect(0, 0, S, S);
      o.globalCompositeOperation = 'source-over';
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = this.bg; ctx.fillRect(0, 0, this.w, this.h);
      const cx = this.w * this.cxf, cy = this.h * this.cyf, D = this.D;
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(this.off, cx - D / 2, cy - D / 2, D, D);
      ctx.globalCompositeOperation = 'source-over';
    }
    k_moon(o, T, c) {
      const R0 = this.S * .27;
      let g = o.createRadialGradient(c, c, R0 * .95, c, c, R0 * 2);
      g.addColorStop(0, 'rgba(255,244,222,.28)'); g.addColorStop(1, 'rgba(255,244,222,0)');
      o.fillStyle = g; o.fillRect(0, 0, this.S, this.S);
      o.save(); o.beginPath(); o.arc(c, c, R0, 0, TAU); o.clip();
      g = o.createRadialGradient(c - R0 * .35, c - R0 * .4, R0 * .1, c, c, R0 * 1.05);
      g.addColorStop(0, '#FFF9EE'); g.addColorStop(.55, '#E6DFD0'); g.addColorStop(1, '#B9B1A1');
      o.fillStyle = g; o.fillRect(c - R0, c - R0, R0 * 2, R0 * 2);
      o.fillStyle = 'rgba(122,114,102,.30)';
      for (const m of this.maria) { o.beginPath(); o.ellipse(m.x, m.y, m.rx, m.ry, m.rot, 0, TAU); o.fill(); }
      for (const k of this.craters) {
        o.fillStyle = 'rgba(108,101,90,.24)'; o.beginPath(); o.arc(k.x, k.y, k.r, 0, TAU); o.fill();
        o.strokeStyle = 'rgba(255,255,255,.28)'; o.lineWidth = Math.max(.8, k.r * .2);
        o.beginPath(); o.arc(k.x, k.y, k.r, Math.PI * 1.05, Math.PI * 1.85); o.stroke();
      }
      g = o.createLinearGradient(c - R0, c, c + R0, c);
      g.addColorStop(0, 'rgba(13,11,36,0)'); g.addColorStop(.62, 'rgba(13,11,36,0)'); g.addColorStop(1, 'rgba(13,11,36,.6)');
      o.fillStyle = g; o.fillRect(c - R0, c - R0, R0 * 2, R0 * 2);
      o.restore();
    }
    k_earth(o, T, c, u) {
      const R0 = this.S * .27;
      let g = o.createRadialGradient(c, c, R0 * .96, c, c, R0 * 1.45);
      g.addColorStop(0, 'rgba(120,196,255,.38)'); g.addColorStop(1, 'rgba(120,196,255,0)');
      o.fillStyle = g; o.fillRect(0, 0, this.S, this.S);
      o.save(); o.beginPath(); o.arc(c, c, R0, 0, TAU); o.clip();
      g = o.createRadialGradient(c - R0 * .3, c - R0 * .35, R0 * .1, c, c, R0);
      g.addColorStop(0, '#4A92EA'); g.addColorStop(.6, '#1E56A8'); g.addColorStop(1, '#0D2A63');
      o.fillStyle = g; o.fillRect(c - R0, c - R0, R0 * 2, R0 * 2);
      for (const l of this.land) { o.fillStyle = l.desert ? 'rgba(201,178,124,.85)' : 'rgba(78,154,100,.92)'; o.beginPath(); o.arc(l.x, l.y, l.r, 0, TAU); o.fill(); }
      const drift = reduced ? 0 : Math.sin(T * .00008) * R0 * .05;
      o.fillStyle = 'rgba(255,255,255,.55)';
      for (const cl of this.clouds) { o.beginPath(); o.ellipse(cl.x + drift, cl.y, cl.rx, cl.ry, cl.rot, 0, TAU); o.fill(); }
      g = o.createLinearGradient(c - R0, c, c + R0, c);
      g.addColorStop(0, 'rgba(13,11,36,0)'); g.addColorStop(.58, 'rgba(13,11,36,0)'); g.addColorStop(1, 'rgba(13,11,36,.72)');
      o.fillStyle = g; o.fillRect(c - R0, c - R0, R0 * 2, R0 * 2);
      o.restore();
      o.strokeStyle = 'rgba(150,215,255,.55)'; o.lineWidth = 2 * u;
      o.beginPath(); o.arc(c, c, R0, 0, TAU); o.stroke();
    }
    k_nebula(o, T) {
      const S = this.S;
      o.globalCompositeOperation = 'lighter';
      for (const g0 of this.gas) {
        const x = g0.x + Math.sin(T * .00012 + g0.f) * S * .018, y = g0.y + Math.cos(T * .0001 + g0.f) * S * .018;
        const g = o.createRadialGradient(x, y, 0, x, y, g0.r);
        g.addColorStop(0, 'rgba(' + g0.col + ',' + g0.a + ')'); g.addColorStop(.55, 'rgba(' + g0.col + ',' + (g0.a * .35).toFixed(3) + ')'); g.addColorStop(1, 'rgba(' + g0.col + ',0)');
        o.fillStyle = g; o.fillRect(x - g0.r, y - g0.r, g0.r * 2, g0.r * 2);
      }
      o.globalCompositeOperation = 'source-over';
      for (const d of this.dust) {
        const g = o.createRadialGradient(d.x, d.y, 0, d.x, d.y, d.r);
        g.addColorStop(0, 'rgba(10,8,30,.5)'); g.addColorStop(1, 'rgba(10,8,30,0)');
        o.fillStyle = g; o.fillRect(d.x - d.r, d.y - d.r, d.r * 2, d.r * 2);
      }
      this.spikes(o);
    }
    k_milkyway(o, T, c) {
      const S = this.S;
      o.save(); o.translate(c, c); o.rotate(-.55);
      o.globalCompositeOperation = 'lighter';
      o.save(); o.scale(1, .2);
      let g = o.createRadialGradient(0, 0, 0, 0, 0, S * .6);
      g.addColorStop(0, 'rgba(255,230,204,.30)'); g.addColorStop(.5, 'rgba(190,170,255,.10)'); g.addColorStop(1, 'rgba(190,170,255,0)');
      o.fillStyle = g; o.fillRect(-S, -S * 3, S * 2, S * 6);
      o.restore();
      for (const p of this.band) { o.fillStyle = 'rgba(255,246,232,' + p.a + ')'; o.beginPath(); o.arc(p.x, p.y, p.r, 0, TAU); o.fill(); }
      o.globalCompositeOperation = 'source-over';
      o.save(); o.translate(0, S * .012); o.scale(1, .045);
      g = o.createRadialGradient(0, 0, 0, 0, 0, S * .5);
      g.addColorStop(0, 'rgba(10,8,30,.55)'); g.addColorStop(1, 'rgba(10,8,30,0)');
      o.fillStyle = g; o.fillRect(-S, -S * 12, S * 2, S * 24);
      o.restore();
      o.restore();
    }
    k_saturn(o, T, c) {
      const S = this.S, R0 = S * .15, tilt = -.38;
      const bands = [[1.3, 1.52, 'rgba(214,190,140,.5)'], [1.56, 1.9, 'rgba(238,216,170,.82)'], [1.97, 2.22, 'rgba(206,182,136,.5)']];
      const rings = front => {
        o.save(); o.translate(c, c); o.rotate(tilt); o.scale(1, .3);
        o.beginPath(); if (front) o.rect(-S, 0, S * 2, S * 4); else o.rect(-S, -S * 4, S * 2, S * 4); o.clip();
        for (const [a, b, col] of bands) { o.beginPath(); o.arc(0, 0, R0 * b, 0, TAU); o.arc(0, 0, R0 * a, 0, TAU, true); o.fillStyle = col; o.fill('evenodd'); }
        o.restore();
      };
      let g = o.createRadialGradient(c, c, R0, c, c, R0 * 2.6);
      g.addColorStop(0, 'rgba(255,226,170,.16)'); g.addColorStop(1, 'rgba(255,226,170,0)');
      o.fillStyle = g; o.fillRect(0, 0, S, S);
      rings(false);
      o.save(); o.beginPath(); o.arc(c, c, R0, 0, TAU); o.clip();
      o.fillStyle = '#E2C38C'; o.fillRect(c - R0, c - R0, R0 * 2, R0 * 2);
      o.save(); o.translate(c, c); o.rotate(tilt);
      for (let k = -7; k <= 7; k++) { o.fillStyle = k % 2 ? 'rgba(176,132,76,.32)' : 'rgba(255,242,214,.22)'; o.fillRect(-R0 * 1.2, k * R0 * .15, R0 * 2.4, R0 * .075); }
      o.restore();
      g = o.createRadialGradient(c - R0 * .4, c - R0 * .45, R0 * .1, c, c, R0 * 1.05);
      g.addColorStop(0, 'rgba(255,250,235,.35)'); g.addColorStop(.6, 'rgba(255,250,235,0)'); g.addColorStop(1, 'rgba(13,11,36,.55)');
      o.fillStyle = g; o.fillRect(c - R0, c - R0, R0 * 2, R0 * 2);
      o.restore();
      rings(true);
    }
    k_galaxy(o, T, c) {
      const S = this.S;
      o.save(); o.translate(c, c); o.rotate(-.5 + (reduced ? 0 : T * .000006)); o.scale(1, .62);
      o.globalCompositeOperation = 'lighter';
      let g = o.createRadialGradient(0, 0, 0, 0, 0, S * .45);
      g.addColorStop(0, 'rgba(255,232,200,.55)'); g.addColorStop(.12, 'rgba(255,210,180,.22)'); g.addColorStop(.5, 'rgba(150,130,255,.08)'); g.addColorStop(1, 'rgba(150,130,255,0)');
      o.fillStyle = g; o.fillRect(-S, -S, S * 2, S * 2);
      for (const p of this.spiral) { o.fillStyle = 'rgba(' + p.col + ',' + p.a + ')'; o.beginPath(); o.arc(p.x, p.y, p.r, 0, TAU); o.fill(); }
      o.restore();
      o.globalCompositeOperation = 'source-over';
    }
    k_stars(o) { this.spikes(o); }
    k_universe(o, T) {
      const S = this.S;
      o.globalCompositeOperation = 'lighter';
      for (const g0 of this.gas) {
        const x = g0.x + Math.sin(T * .0001 + g0.f) * S * .015, y = g0.y;
        const g = o.createRadialGradient(x, y, 0, x, y, g0.r);
        g.addColorStop(0, 'rgba(' + g0.col + ',' + g0.a + ')'); g.addColorStop(1, 'rgba(' + g0.col + ',0)');
        o.fillStyle = g; o.fillRect(x - g0.r, y - g0.r, g0.r * 2, g0.r * 2);
      }
      for (const gx of this.galaxies) {
        o.save(); o.translate(gx.x, gx.y); o.rotate(gx.tilt); o.scale(1, gx.flat);
        const g = o.createRadialGradient(0, 0, 0, 0, 0, gx.rx);
        const col = gx.warm ? '255,224,190' : '190,200,255';
        g.addColorStop(0, 'rgba(' + col + ',.9)'); g.addColorStop(.25, 'rgba(' + col + ',.35)'); g.addColorStop(1, 'rgba(' + col + ',0)');
        o.fillStyle = g; o.beginPath(); o.arc(0, 0, gx.rx, 0, TAU); o.fill();
        o.restore();
      }
      o.globalCompositeOperation = 'source-over';
      this.spikes(o);
    }
    k_aurora(o, T) {
      const S = this.S, N = 72, w = S / N;
      o.globalCompositeOperation = 'lighter';
      for (const cu of this.curtains) {
        for (let i = 0; i < N; i++) {
          const x = i * w;
          const top = S * (.2 + .07 * Math.sin(i * .16 + cu.ph + T * .0003) + .05 * Math.sin(i * .05 + cu.ph * 2));
          const len = S * (.32 + .1 * Math.sin(i * .11 + cu.ph + T * .00022));
          const g = o.createLinearGradient(0, top, 0, top + len);
          g.addColorStop(0, 'rgba(155,123,255,0)'); g.addColorStop(.22, 'rgba(155,123,255,.07)');
          g.addColorStop(.62, 'rgba(' + cu.col + ',' + cu.a + ')'); g.addColorStop(1, 'rgba(' + cu.col + ',0)');
          o.fillStyle = g; o.fillRect(x - w * .6, top, w * 2.2, len);
        }
      }
      o.globalCompositeOperation = 'source-over';
      this.spikes(o);
    }
  }

  const skies = [];
  const skyIO = 'IntersectionObserver' in window ? new IntersectionObserver(es => es.forEach(e => {
    const s = e.target._sky; s.visible = e.isIntersecting;
    if (e.isIntersecting) { if (!s.ready) s.resize(); s.draw(performance.now()); }
  }), { rootMargin: '120px' }) : null;
  $$('canvas.sky').forEach(cv => { const s = new Sky(cv); cv._sky = s; skies.push(s); if (skyIO) skyIO.observe(cv); else s.visible = true; });
  let lastFrame = 0;
  const loop = t => { if (t - lastFrame > 45) { lastFrame = t; for (const s of skies) if (s.visible && !document.hidden) s.draw(t); } requestAnimationFrame(loop); };
  if (!reduced && skies.length) requestAnimationFrame(loop);

  /* ---------- slide switchers ---------- */
  $$('[data-slide-group]').forEach(group => {
    const canvas = document.getElementById(group.dataset.target);
    const caption = group.dataset.caption ? document.getElementById(group.dataset.caption) : null;
    $$('[data-slide]', group).forEach(btn => btn.addEventListener('click', () => {
      if (canvas && canvas._sky) canvas._sky.setKind(btn.dataset.slide);
      $$('[data-slide]', group).forEach(x => x.setAttribute('aria-pressed', String(x === btn)));
      if (caption) caption.innerHTML = '<span class="spec">' + esc(btn.dataset.label || '') + '</span><p>' + esc(btn.dataset.note || '') + '</p>';
    }));
  });

  /* ---------- delivery dates ---------- */
  const eta = C.eta || [8, 13];
  function addBiz(d, n) { const x = new Date(d); while (n > 0) { x.setDate(x.getDate() + 1); const w = x.getDay(); if (w !== 0 && w !== 6) n--; } return x; }
  const fmt = (d, o) => d.toLocaleDateString('en-US', o || { month: 'short', day: 'numeric' });
  (() => {
    const now = new Date(), a = addBiz(now, eta[0]), b = addBiz(now, eta[1]);
    const text = a.getMonth() === b.getMonth() ? fmt(a) + '–' + b.getDate() : fmt(a) + ' – ' + fmt(b);
    $$('[data-eta]').forEach(el => { el.textContent = text; });
    let year = now.getFullYear(), xmas = new Date(year, 11, 24);
    if (now > xmas) xmas = new Date(++year, 11, 24);
    const d = new Date(xmas);
    while (addBiz(d, eta[1]) > xmas) d.setDate(d.getDate() - 1);
    $$('[data-cutoff]').forEach(el => { el.textContent = fmt(d, { weekday: 'long', month: 'short', day: 'numeric' }); });
    $$('[data-cutoff-short]').forEach(el => { el.textContent = fmt(d); });

    /* countdown to the end of the cut-off day */
    const boxes = $$('[data-countdown]');
    if (boxes.length) {
      const xmasEnd = new Date(d); xmasEnd.setHours(23, 59, 59, 0);
      const pad = n => String(n).padStart(2, '0');
      const tick = () => {
        boxes.forEach(b => {
          const fixed = b.dataset.end ? new Date(b.dataset.end) : null;
          const ms = (fixed && !isNaN(fixed) ? fixed : xmasEnd) - new Date();
          if (ms <= 0) { b.hidden = true; return; }
          b.hidden = false;
          const mins = Math.floor(ms / 60000);
          $('[data-cd-days]', b).textContent = Math.floor(mins / 1440);
          $('[data-cd-hours]', b).textContent = pad(Math.floor(mins % 1440 / 60));
          $('[data-cd-mins]', b).textContent = pad(mins % 60);
        });
      };
      tick(); setInterval(tick, 30000);
    }
  })();

  /* ---------- cart ---------- */
  const drawer = $('#cart-drawer'), scrim = $('#cart-scrim');
  let returnFocus = null;
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  async function getCart() { const r = await fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } }); return r.json(); }
  function thumb(url) { if (!url) return MARK; return '<img src="' + esc(url + (url.includes('?') ? '&' : '?') + 'width=112') + '" alt="" width="56" height="56" loading="lazy">'; }
  function renderCart(cart) {
    $$('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
    if (!drawer) return;
    const body = $('[data-cart-lines]', drawer);
    body.innerHTML = cart.items.length ? cart.items.map((it, i) => {
      const sub = it.variant_title && it.variant_title !== 'Default Title' ? '<span class="line-sub">' + esc(it.variant_title) + '</span>' : '';
      return '<div class="line"><div class="line-thumb">' + thumb(it.image) + '</div><div><span class="line-title">' + esc(it.product_title) + '</span>' + sub +
        '<span class="qty"><button type="button" data-line="' + (i + 1) + '" data-qty="' + (it.quantity - 1) + '" aria-label="Remove one">−</button><span>' + it.quantity + '</span>' +
        '<button type="button" data-line="' + (i + 1) + '" data-qty="' + (it.quantity + 1) + '" aria-label="Add one">+</button></span>' +
        '<button type="button" class="remove" data-line="' + (i + 1) + '" data-qty="0">Remove</button></div><span class="line-price">' + money(it.final_line_price) + '</span></div>';
    }).join('') : '<p class="empty">Your cart is empty.</p>';
    const up = C.upsell, upEl = $('[data-cart-upsell]', drawer);
    const showUp = up && up.id && cart.item_count > 0 && !cart.items.some(it => it.variant_id === up.id);
    upEl.innerHTML = showUp ? '<div class="upsell"><span><strong>' + esc(up.title) + '</strong><br>Add it for ' + money(up.price) + '</span><button type="button" data-add-variant="' + up.id + '">Add</button></div>' : '';
    $('[data-cart-subtotal]', drawer).textContent = money(cart.total_price);
    $('[data-checkout]', drawer).disabled = cart.item_count === 0;
  }
  function openCart(from) {
    if (!drawer) { window.location.href = routes.cart; return; }
    returnFocus = from || document.activeElement;
    drawer.hidden = false; scrim.hidden = false;
    requestAnimationFrame(() => { drawer.classList.add('open'); scrim.classList.add('open'); drawer.focus(); });
  }
  function closeCart() {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('open'); scrim.classList.remove('open');
    setTimeout(() => { drawer.hidden = true; scrim.hidden = true; }, reduced ? 0 : 300);
    if (returnFocus && returnFocus.focus) returnFocus.focus();
  }
  async function addItems(items) {
    const r = await fetch(routes.cartAdd + '.js', { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ items }) });
    const data = await r.json();
    if (!r.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
    return data;
  }
  async function changeLine(line, quantity) {
    const r = await fetch(routes.cartChange + '.js', { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ line, quantity }) });
    return r.json();
  }
  document.addEventListener('click', async e => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && drawer) { e.preventDefault(); renderCart(await getCart()); openCart(opener); return; }
    if (e.target.closest('[data-cart-close]')) { closeCart(); return; }
    const lineBtn = e.target.closest('[data-line]');
    if (lineBtn && drawer && drawer.contains(lineBtn)) {
      drawer.classList.add('is-busy');
      try { renderCart(await changeLine(+lineBtn.dataset.line, +lineBtn.dataset.qty)); } finally { drawer.classList.remove('is-busy'); }
      return;
    }
    const addBtn = e.target.closest('[data-add-variant]');
    if (addBtn) {
      drawer && drawer.classList.add('is-busy');
      try { await addItems([{ id: +addBtn.dataset.addVariant, quantity: 1 }]); renderCart(await getCart()); } finally { drawer && drawer.classList.remove('is-busy'); }
    }
  });
  if (scrim) scrim.addEventListener('click', closeCart);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeCart(); });

  /* ---------- product galleries ---------- */
  function initGallery(g) {
    const track = $('[data-pg-track]', g), slides = $$('.pg-slide', track), thumbs = $$('[data-pg-thumb]', g), idx = $('[data-pg-index]', g);
    let current = 0;
    const mark = i => {
      current = i;
      thumbs.forEach((t, k) => t.setAttribute('aria-current', String(k === i)));
      if (idx) idx.textContent = i + 1;
      const th = thumbs[i], strip = th && th.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        const left = th.offsetLeft - strip.offsetLeft, right = left + th.offsetWidth;
        if (left < strip.scrollLeft) strip.scrollLeft = left;
        else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = right - strip.clientWidth;
      }
    };
    const go = (i, smooth) => {
      if (!slides.length) return;
      i = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth && !reduced ? 'smooth' : 'auto' });
      mark(i);
    };
    g._show = (mediaId, smooth) => { const i = slides.findIndex(s => s.dataset.mediaId === String(mediaId)); if (i > -1) go(i, smooth); };
    thumbs.forEach((t, i) => t.addEventListener('click', () => go(i, true)));
    const prev = $('[data-pg-prev]', g), next = $('[data-pg-next]', g);
    if (prev) prev.addEventListener('click', () => go(current - 1, true));
    if (next) next.addEventListener('click', () => go(current + 1, true));
    track.addEventListener('keydown', e => { if (e.key === 'ArrowRight') go(current + 1, true); if (e.key === 'ArrowLeft') go(current - 1, true); });
    let t0;
    track.addEventListener('scroll', () => { clearTimeout(t0); t0 = setTimeout(() => { const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth)); if (i !== current) mark(i); }, 80); }, { passive: true });
    if (g.dataset.start) g._show(g.dataset.start, false);
  }
  $$('[data-gallery]').forEach(initGallery);

  function syncVariant(form) {
    const main = form.querySelector('input[name="id"]:checked');
    const scope = form.closest('[data-buy-scope]');
    if (!main || !scope) return;
    const g = $('[data-gallery]', scope);
    if (g && g._show && main.dataset.media) g._show(main.dataset.media, true);
    const box = $('[data-pdp-price]', scope);
    if (box) {
      const price = +main.dataset.price, was = +main.dataset.compare;
      $('[data-price-now]', box).textContent = money(price);
      const wasEl = $('[data-price-was]', box), saveEl = $('[data-price-save]', box);
      const onSale = was > price;
      wasEl.hidden = !onSale; saveEl.hidden = !onSale;
      if (onSale) { wasEl.textContent = money(was); saveEl.textContent = 'Save ' + money(was - price); }
    }
  }

  /* ---------- buy boxes ---------- */
  function updateTotal(form) {
    const main = form.querySelector('input[name="id"]:checked');
    const qtyEl = form.querySelector('[data-qty-input]');
    const qty = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;
    let total = main ? +main.dataset.price * qty : 0;
    $$('[data-addon]:checked', form).forEach(a => { total += +a.dataset.price; });
    const btn = form.querySelector('[data-total]');
    if (btn && !btn.disabled) btn.textContent = (btn.dataset.label || 'Add to cart') + ' · ' + money(total);
  }
  $$('form[data-buybox]').forEach(form => {
    updateTotal(form);
    form.addEventListener('change', e => { updateTotal(form); if (e.target.name === 'id') syncVariant(form); });
    form.addEventListener('input', e => { if (e.target.matches('[data-qty-input]')) updateTotal(form); });
    $$('[data-qty-step]', form).forEach(b => b.addEventListener('click', () => {
      const q = form.querySelector('[data-qty-input]');
      q.value = Math.max(1, Math.min(20, (parseInt(q.value, 10) || 1) + +b.dataset.qtyStep));
      updateTotal(form);
    }));
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const btn = form.querySelector('[data-total]'), err = form.querySelector('[data-error]');
      const items = [];
      const main = form.querySelector('input[name="id"]:checked');
      const qtyEl = form.querySelector('[data-qty-input]');
      const qty = qtyEl ? Math.max(1, Math.min(20, parseInt(qtyEl.value, 10) || 1)) : 1;
      if (main) items.push({ id: +main.value, quantity: qty });
      $$('[data-addon]:checked', form).forEach(a => items.push({ id: +a.value, quantity: 1 }));
      if (!items.length) return;
      btn.disabled = true; if (err) err.hidden = true;
      try {
        await addItems(items);
        renderCart(await getCart());
        openCart(btn);
      } catch (ex) {
        if (err) { err.textContent = ex.message; err.hidden = false; }
      } finally { btn.disabled = false; updateTotal(form); }
    });
  });

  /* ---------- sticky buy bar (phones) ---------- */
  const sticky = $('#sticky-buy'), firstBox = $('form[data-buybox]');
  if (sticky && firstBox && 'IntersectionObserver' in window) {
    new IntersectionObserver(es => es.forEach(e => {
      const show = !e.isIntersecting && window.innerWidth < 900;
      sticky.hidden = !show; document.body.classList.toggle('has-sticky', show);
    })).observe(firstBox);
    $('[data-sticky-go]', sticky).addEventListener('click', () => firstBox.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' }));
  }
  $$('[data-scroll-buy]').forEach(a => a.addEventListener('click', e => {
    const box = $('form[data-buybox]');
    if (box) { e.preventDefault(); box.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' }); }
  }));

  /* ---------- header menu ---------- */
  const menuBtn = $('#menu-btn'), nav = $('#site-nav');
  if (menuBtn && nav) menuBtn.addEventListener('click', () => { const open = nav.classList.toggle('open'); menuBtn.setAttribute('aria-expanded', String(open)); });

  /* ---------- YouTube: load the player only when tapped ---------- */
  $$('[data-yt]').forEach(btn => btn.addEventListener('click', () => {
    const f = document.createElement('iframe');
    f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(btn.dataset.yt) + '?autoplay=1&playsinline=1&rel=0&modestbranding=1';
    f.title = btn.getAttribute('aria-label') || 'Video';
    f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    f.allowFullscreen = true;
    btn.parentElement.replaceChildren(f);
  }));

  /* ---------- autoplay videos: muted loop, tap for sound ---------- */
  $$('video[data-autoplay]').forEach(v => {
    v.muted = true;
    const tryPlay = () => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
    if (reduced) { v.removeAttribute('autoplay'); v.controls = true; } else tryPlay();
    const btn = v.parentElement.querySelector('[data-sound]');
    if (btn) btn.addEventListener('click', () => {
      v.muted = !v.muted;
      if (!v.muted) { v.currentTime = 0; tryPlay(); }
      btn.setAttribute('aria-pressed', String(!v.muted));
      btn.textContent = v.muted ? 'Tap for sound' : 'Sound on';
    });
  });

  /* ---------- before / after ---------- */
  $$('[data-ba]').forEach(ba => {
    const range = $('input[type="range"]', ba);
    range.addEventListener('input', () => ba.style.setProperty('--pos', range.value + '%'));
  });
})();
